import styles from './SwDeliverySuccesRate.module.css';
import { useGetPipelineSuccessStats } from '../hooks/use-get-pipelines-success-stats';
import { useState } from 'react';
import { useTheme } from '../../../hooks/useTheme';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Loading } from '@dsi/react-eds';

interface SwDeliverySuccessRateProps {
  selectedTimeRange?: string;
  startDate?: string;
  endDate?: string;
  onMaximize?: () => void;
  isMaximized?: boolean;
}

const SwDeliverySuccessRate = ({ selectedTimeRange, startDate, endDate }: SwDeliverySuccessRateProps) => {
  const queryParams = startDate != null && endDate != null ? { startDate, endDate } : { timeRangeText: selectedTimeRange };
  const { data: stats, isLoading, isError } = useGetPipelineSuccessStats(queryParams);
  const [hoveredPill, setHoveredPill] = useState<'success' | 'non-success' | null>(null);
  const { theme } = useTheme();

  return (
    <div>
      {isLoading && (
        <div className={styles.loadingContainer}>
          <ReactEDSWrapper component={Loading} size="large" />
        </div>
      )}
      {isError && <div className={styles.loadingText}>Error while loading the success rate.</div>}
      {stats &&
        !isLoading &&
        !isError &&
        (() => {
          const successItem = stats.find((item) => item.status === 'success');
          const successfulPercentage = successItem ? successItem.percentage : 0;
          const nonSuccessfulPercentage = Math.round((100 - successfulPercentage) * 10) / 10;

          // donut constants
          const radius = 80;
          const circumference = 2 * Math.PI * radius;
          const gapLength = 3;

          //arc lengths vs rate percentages values calculations
          const successArcLength = (successfulPercentage / 100) * circumference - gapLength;
          const nonSuccessArcLength = (nonSuccessfulPercentage / 100) * circumference - gapLength;

          const successDasharray =
            successfulPercentage === 100 ? `${circumference} 0` : `${successArcLength} ${circumference - successArcLength}`;

          const nonSuccessDasharray =
            nonSuccessfulPercentage === 0 ? `0 ${circumference}` : `${nonSuccessArcLength} ${circumference - nonSuccessArcLength}`;

          const nonSuccessDashoffset = nonSuccessfulPercentage === 0 ? 0 : -(successArcLength + gapLength);

          return (
            <div className={styles.customDonutContainer}>
              {/* Legend Pills */}
              <div className={styles.legendContainer}>
                <div
                  className={styles.legendPill}
                  style={{
                    backgroundColor: hoveredPill === 'success' ? (theme ? '#dcdcdc' : '#838080') : 'transparent',
                    borderColor: hoveredPill === 'success' ? '#329864' : undefined
                  }}
                  onMouseEnter={() => setHoveredPill('success')}
                  onMouseLeave={() => setHoveredPill(null)}
                >
                  <div className={styles.legendIndicator} style={{ backgroundColor: '#329864' }}></div>
                  <span className={styles.legendText}>Success</span>
                </div>
                <div
                  className={styles.legendPill}
                  style={{
                    backgroundColor: hoveredPill === 'non-success' ? (theme ? '#dcdcdc' : '#838080') : 'transparent',
                    borderColor: hoveredPill === 'non-success' ? '#329864' : undefined
                  }}
                  onMouseEnter={() => setHoveredPill('non-success')}
                  onMouseLeave={() => setHoveredPill(null)}
                >
                  <div className={styles.legendIndicator} style={{ backgroundColor: '#A9A9A9' }}></div>
                  <span className={styles.legendText}>Non Success</span>
                </div>
              </div>

              {/* Custom Donut Chart */}
              <div className={styles.donutChart}>
                <svg className={styles.donutSvg} viewBox="0 0 200 200">
                  {/* Background circle */}
                  <circle cx="100" cy="100" r="80" fill="none" stroke={theme ? 'transparent' : '#282828'} strokeWidth="20" />

                  {/* Success arc */}
                  <circle
                    cx="100"
                    cy="100"
                    r={radius}
                    fill="none"
                    stroke={hoveredPill === 'non-success' ? '#b6e0bf' : '#329864'}
                    strokeWidth={hoveredPill === 'non-success' ? '8' : '20'}
                    strokeDasharray={successDasharray}
                    strokeDashoffset="0"
                    transform="rotate(-90 100 100)"
                    className={styles.successArc}
                  />

                  {/* Non-success arc */}
                  <circle
                    cx="100"
                    cy="100"
                    r={radius}
                    fill="none"
                    stroke={hoveredPill === 'success' ? '#D0D0D0' : '#A9A9A9'}
                    strokeWidth={hoveredPill === 'success' ? '8' : '20'}
                    strokeDasharray={nonSuccessDasharray}
                    strokeDashoffset={nonSuccessDashoffset}
                    transform="rotate(-90 100 100)"
                    className={styles.nonSuccessArc}
                  />
                </svg>

                {/* Center text */}
                <div className={styles.donutCenter}>
                  <div className={styles.donutCenterLabel}>{hoveredPill === 'non-success' ? 'Non Success' : 'Success'}</div>
                  <div className={styles.donutCenterValue}>
                    {hoveredPill === 'non-success' ? nonSuccessfulPercentage : successfulPercentage}
                  </div>
                  <div className={styles.donutCenterUnit}>%</div>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default SwDeliverySuccessRate;
